import Vue from 'vue';
import findIndex from 'lodash/findIndex';
import Projects from '@/resources/projects';
import jsona from '@/lib/jsona';
import RootStateCommit from '@/store/mixins/rootstate-commit';
import { setDefaultTimezone } from '@/lib/timezones';

const MODULE_NAME = 'projects';
const DEFAULT_STATE = {
  projects: [],
  selectedLicenceAreaOrZone: {},
  activeProjectCode: null
};

function changeProjectAndUpdateState(state, projectCode, attributes) {
  const projectIndex = state.findIndex(project => project.code === projectCode);
  const newProject = { ...state[projectIndex], ...attributes };
  Vue.set(state, projectIndex, newProject);
}

export default {
  namespaced: true,
  state: { ...DEFAULT_STATE },

  /* eslint no-param-reassign: ["error", { "props": false }] */
  mutations: {
    parseResponseData(state, response) {
      state.projects = jsona.deserialize(response);
    },
    changeSelectedLicenceAreaOrZone(state, data) {
      state.selectedLicenceAreaOrZone = data;
    },
    setActiveProjectCode(state, projectCode) {
      Vue.set(state, 'activeProjectCode', projectCode);
    },
    resetStore(state) {
      Object.assign(state, DEFAULT_STATE);
    },
    removeProject(state, projectCode) {
      changeProjectAndUpdateState(state.projects, projectCode, { removed: true });
    },
    markProjectAsPermanentlyRemoved(state, projectCode) {
      changeProjectAndUpdateState(state.projects, projectCode, { permanentlyRemoved: true });
    },
    rollbackRemoveProject(state, projectCode) {
      changeProjectAndUpdateState(state.projects, projectCode, { removed: false });
    },
    updateProjectsLicenceAreas(state, { projectCode, licenceAreas }) {
      const index = findIndex(state, { code: projectCode });
      const project = state[index];

      if (project) {
        project.licenceAreas = licenceAreas;
        state.splice(index, 1, project);
      }
    },
    updateProjectsLicenceAreaZones(state, { projectCode, licenceAreaCode, zones }) {
      const index = findIndex(state, { code: projectCode });
      const project = state[index];
      const licenceAreaIndex = findIndex(project.licenceAreas, {
        code: licenceAreaCode
      });

      if (licenceAreaIndex) {
        project.licenceAreas[licenceAreaIndex].zones = zones;
        state.splice(index, 1, project);
      }
    }
  },
  actions: {
    fetchData({ commit, dispatch }, projectCode = null) {
      RootStateCommit(commit, 'startLoading', MODULE_NAME);
      Projects.get({ include: 'licence_areas,licence_areas.zones' }).then(
        response => {
          commit('parseResponseData', response.data);

          if (projectCode) dispatch('setActiveProjectCode', projectCode);

          return RootStateCommit(commit, 'stopLoading', MODULE_NAME);
        },
        () => RootStateCommit(commit, 'setResponseError', MODULE_NAME)
      );
    },
    removeProject({ commit }, projectCode) {
      commit('removeProject', projectCode);
      return Projects.delete({ code: projectCode }).then(
        () => commit('markProjectAsPermanentlyRemoved', projectCode),
        () => commit('rollbackRemoveProject', projectCode)
      );
    },
    setActiveProjectCode({ dispatch }, projectCode) {
      dispatch('setTimezone', projectCode);

      return true;
    },
    setTimezone({ state }, projectCode) {
      const timezone = state.projects.find(project => project.code === projectCode).timeZone;

      setDefaultTimezone(timezone);
    }
  },
  getters: {
    projects: state => [...state.projects].filter(project => !project.removed),
    licenceAreas: state =>
      state.projects.reduce((arrayOfLicenceAreasAndZones, project) => {
        const { licenceAreas, code } = project;

        arrayOfLicenceAreasAndZones[code] = arrayOfLicenceAreasAndZones[code] || [];
        arrayOfLicenceAreasAndZones[code] = licenceAreas;
        return arrayOfLicenceAreasAndZones;
      }, {}),
    activeProjectTimezone: state => {
      if (state.activeProjectCode && state.projects.length) {
        const { timeZone } = state.projects.find(
          project => project.code === state.activeProjectCode
        );

        return timeZone;
      }
      return null;
    }
  }
};
